// ============================================================================
// APPROVAL SERVICE - Manages content approval queue CRUD operations
// ============================================================================

import { supabase } from "@/integrations/supabase/client";

// ============================================================================
// TYPES
// ============================================================================

export interface ApprovalQueueItem {
  id: string;
  contentType: "show" | "episode";
  contentId: string;
  userId: string;
  status: "pending" | "pending_distribution" | "approved" | "delivered" | "rejected";
  assignedPlatforms: string[];
  reviewedBy: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
  // Joined fields
  contentTitle?: string;
  contentStatus?: string;
  podcasterName?: string;
  podcasterEmail?: string;
  reviewerName?: string;
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Fetch rows by a set of IDs without overflowing the request URL length limit.
 * PostgREST encodes `.in()` filters into the GET query string, so a few hundred
 * IDs produce a URL the server rejects (414) — which would silently return no
 * rows. Chunking keeps each request small and merges the results.
 */
async function fetchByIdsInChunks(
  table: "shows" | "episodes" | "profiles",
  columns: string,
  ids: string[],
  chunkSize = 80,
): Promise<any[]> {
  if (ids.length === 0) return [];
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    chunks.push(ids.slice(i, i + chunkSize));
  }
  const results = await Promise.all(
    chunks.map((chunk) => supabase.from(table).select(columns).in("id", chunk)),
  );
  return results.flatMap((r) => r.data || []);
}

function mapRow(row: any): ApprovalQueueItem {
  return {
    id: row.id,
    contentType: row.content_type,
    contentId: row.content_id,
    userId: row.user_id,
    status: row.status,
    assignedPlatforms: row.assigned_platforms || [],
    reviewedBy: row.reviewed_by,
    reviewedAt: row.reviewed_at,
    rejectionReason: row.rejection_reason,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    contentTitle: row.content_title,
    podcasterName: row.podcaster_name,
    podcasterEmail: row.podcaster_email,
  };
}

// ============================================================================
// SERVICE FUNCTIONS
// ============================================================================

/**
 * Submit content to the approval queue (called when podcaster creates show/episode)
 */
export async function submitForApproval(
  contentType: "show" | "episode",
  contentId: string
): Promise<ApprovalQueueItem> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  const { data, error } = await supabase
    .from("content_approval_queue")
    .insert({
      content_type: contentType,
      content_id: contentId,
      user_id: sessionData.session.user.id,
      status: "pending",
    })
    .select()
    .single();

  if (error) throw error;

  // Update the content's own status to reflect pending approval
  if (contentType === "episode") {
    const { error: statusError } = await supabase
      .from("episodes")
      .update({ status: "pending_approval" })
      .eq("id", contentId);
    if (statusError) console.error("Failed to update episode status to pending_approval:", statusError);
  } else if (contentType === "show") {
    const { error: statusError } = await supabase
      .from("shows")
      .update({ status: "pending_approval" })
      .eq("id", contentId);
    if (statusError) console.error("Failed to update show status to pending_approval:", statusError);
  }

  return mapRow(data);
}

/**
 * Get all approval queue items (admin only)
 * Enriches with content title and podcaster name via separate queries
 */
export async function getApprovalQueue(
  statusFilter?: string
): Promise<ApprovalQueueItem[]> {
  let query = supabase
    .from("content_approval_queue")
    .select("*")
    .order("created_at", { ascending: false });

  if (statusFilter && statusFilter !== "all") {
    query = query.eq("status", statusFilter);
  }

  const { data, error } = await query;
  if (error) throw error;
  if (!data || data.length === 0) return [];

  // Collect content IDs by type for batch lookup
  const showIds = data.filter((r) => r.content_type === "show").map((r) => r.content_id);
  const episodeIds = data.filter((r) => r.content_type === "episode").map((r) => r.content_id);
  const userIds = [...new Set(data.map((r) => r.user_id))];

  // Fetch titles + statuses in parallel (single batch — drop the redundant duplicate fetches)
  const reviewerIds = [...new Set(data.filter((r) => r.reviewed_by).map((r) => r.reviewed_by as string))];

  const [showsData, episodesData, profilesData, reviewersData] = await Promise.all([
    fetchByIdsInChunks("shows", "id, title, status", showIds),
    fetchByIdsInChunks("episodes", "id, title, status", episodeIds),
    fetchByIdsInChunks("profiles", "id, display_name", userIds),
    fetchByIdsInChunks("profiles", "id, display_name", reviewerIds),
  ]);

  const showsMap = new Map<string, { title: string; status: string }>(
    showsData.map((s: any) => [s.id, { title: s.title, status: s.status }])
  );
  const episodesMap = new Map<string, { title: string; status: string }>(
    episodesData.map((e: any) => [e.id, { title: e.title, status: e.status }])
  );
  const profilesMap = new Map<string, string>(
    profilesData.map((p: any) => [p.id, p.display_name])
  );
  const reviewersMap = new Map<string, string>(
    reviewersData.map((p: any) => [p.id, p.display_name])
  );

  return data.map((row) => {
    const item = mapRow(row);
    if (row.content_type === "show") {
      const s = showsMap.get(row.content_id);
      item.contentTitle = s?.title || "Unknown Show";
      item.contentStatus = s?.status;
    } else {
      const e = episodesMap.get(row.content_id);
      item.contentTitle = e?.title || "Unknown Episode";
      item.contentStatus = e?.status;
    }
    item.podcasterName = profilesMap.get(row.user_id) || "Unknown User";
    if (row.reviewed_by) {
      item.reviewerName = reviewersMap.get(row.reviewed_by) || "Unknown Admin";
    }
    return item;
  });
}

/**
 * Get pending count for badge display
 */
export async function getPendingCount(): Promise<number> {
  const { count, error } = await supabase
    .from("content_approval_queue")
    .select("*", { count: "exact", head: true })
    .eq("status", "pending");

  if (error) return 0;
  return count || 0;
}


/**
 * Approve a queue item with assigned platforms, then trigger automation resync
 */
export async function approveContent(
  queueId: string,
  assignedPlatforms: string[]
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  // First, read the queue item to get content details
  const { data: queueItem, error: fetchError } = await supabase
    .from("content_approval_queue")
    .select("content_type, content_id, user_id")
    .eq("id", queueId)
    .single();

  if (fetchError || !queueItem) throw fetchError || new Error("Queue item not found");

  // For episodes, look up the parent show's hosting_platform automatically
  let platformsToAssign = assignedPlatforms;
  if (queueItem.content_type === "episode") {
    const { data: episode } = await supabase
      .from("episodes")
      .select("show_id, audio_url")
      .eq("id", queueItem.content_id)
      .single();

    if (!episode) throw new Error("Episode not found");

    const { data: show } = await supabase
      .from("shows")
      .select("hosting_platform" as any)
      .eq("id", episode.show_id)
      .single();

    const showData = show as any;
    if (!showData?.hosting_platform) {
      throw new Error("Parent show has not been assigned a hosting platform yet. Approve the show first.");
    }

    platformsToAssign = [showData.hosting_platform];

    if (showData.hosting_platform === "movie_night") {
      const mediaUrl = typeof episode.audio_url === "string" ? episode.audio_url.trim() : "";
      if (!mediaUrl || !/\.(mp4|mov|m4v|webm|mkv|mxf|mpeg|mpg|m3u8)(?:[?#].*)?$/i.test(mediaUrl)) {
        throw new Error("Movie Night episodes require a converted video file before approval. Re-convert the YouTube link or upload an MP4 first.");
      }
    }
  }

  // Mark as approved
  const { error } = await supabase
    .from("content_approval_queue")
    .update({
      status: "approved",
      assigned_platforms: platformsToAssign,
      reviewed_by: sessionData.session.user.id,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", queueId);

  if (error) throw error;

  // Activate the content now that it's approved — throw on failure to prevent silent inconsistency
  if (queueItem.content_type === "show") {
    const updatePayload: any = { status: "active" };
    if (assignedPlatforms.length > 0) {
      updatePayload.hosting_platform = assignedPlatforms[0];
    }
    const { error: activateError } = await supabase
      .from("shows")
      .update(updatePayload)
      .eq("id", queueItem.content_id);

    if (activateError) {
      console.error("Failed to activate show after approval:", activateError);
      throw new Error(`Show approved in queue but failed to activate: ${activateError.message}`);
    }
  } else if (queueItem.content_type === "episode") {
    const { error: publishError } = await supabase
      .from("episodes")
      .update({ status: "processing" })
      .eq("id", queueItem.content_id);

    if (publishError) {
      console.error("Failed to publish episode after approval:", publishError);
      throw new Error(`Episode approved in queue but failed to publish: ${publishError.message}`);
    }
  }

  // Enqueue the sync job for server-side background processing
  const resyncPath =
    queueItem.content_type === "show"
      ? `podgo-automation/podcasts/${queueItem.content_id}/resync`
      : `podgo-automation/episodes/${queueItem.content_id}/resync`;

  const { error: enqueueError } = await supabase
    .from("sync_queue")
    .insert({
      content_type: queueItem.content_type,
      content_id: queueItem.content_id,
      queue_id: queueId,
      assigned_platforms: platformsToAssign,
      status: "pending",
    });

  if (enqueueError) {
    console.error("Failed to enqueue sync job:", enqueueError);
    // Non-fatal: content is approved, sync will need manual retry
  }

  // If a show was just approved, release any RSS-imported episodes that have
  // been sitting in `pending_approval` (no queue row yet) into the approval
  // queue so admins can review them individually. Regular-upload episodes
  // already have a queue row and are skipped by the `not.in` filter.
  if (queueItem.content_type === "show") {
    try {
      const { data: pendingEps } = await supabase
        .from("episodes")
        .select("id, user_id")
        .eq("show_id", queueItem.content_id)
        .eq("status", "pending_approval");

      const epIds = (pendingEps ?? []).map((e) => e.id);
      if (epIds.length > 0) {
        const { data: existingQueue } = await supabase
          .from("content_approval_queue")
          .select("content_id")
          .eq("content_type", "episode")
          .in("content_id", epIds);
        const alreadyQueued = new Set((existingQueue ?? []).map((r: any) => r.content_id));
        const toEnqueue = (pendingEps ?? [])
          .filter((e) => !alreadyQueued.has(e.id))
          .map((e) => ({
            content_type: "episode" as const,
            content_id: e.id,
            user_id: e.user_id,
            status: "pending" as const,
          }));
        if (toEnqueue.length > 0) {
          const { error: bulkQueueErr } = await supabase
            .from("content_approval_queue")
            .insert(toEnqueue);
          if (bulkQueueErr) console.error("Failed to enqueue imported episodes:", bulkQueueErr);
        }
      }
    } catch (err) {
      console.error("Failed to release imported episodes for approval:", err);
    }
  }


  // Notify the podcaster via email + in-app (fire-and-forget, mirrors rejectContent)
  try {
    const contentTitle = queueItem.content_type === "show"
      ? (await supabase.from("shows").select("title").eq("id", queueItem.content_id).single()).data?.title
      : (await supabase.from("episodes").select("title").eq("id", queueItem.content_id).single()).data?.title;

    await supabase.functions.invoke("notify-pending-approval", {
      body: {
        type: "content_approved",
        contentType: queueItem.content_type,
        contentTitle: contentTitle || "Untitled",
        podcasterUserId: queueItem.user_id,
        assignedPlatforms: platformsToAssign,
      },
    });
  } catch (err) {
    console.error("Failed to send approval notification:", err);
  }
}

/**
 * Network-admin approval path for shows.
 *
 * Marks the queue item as `pending_distribution` without assigning a hosting
 * platform, activating the show, or enqueueing a sync job. The Podgo Admin
 * later assigns the platform via the regular `approveContent` flow, which is
 * what triggers activation, sync, and the podcaster notification.
 *
 * Episodes do not use this path — episode approval inherits the parent show's
 * already-assigned hosting platform.
 */
export async function markShowPendingDistribution(
  queueId: string
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  const { error } = await supabase
    .from("content_approval_queue")
    .update({
      status: "pending_distribution",
      reviewed_by: sessionData.session.user.id,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", queueId);

  if (error) throw error;
}

/**
 * Reject a queue item with reason
 */
export async function rejectContent(
  queueId: string,
  reason: string
): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) throw new Error("Not authenticated");

  // Fetch queue item details before updating
  const { data: queueItem, error: fetchError } = await supabase
    .from("content_approval_queue")
    .select("content_type, content_id, user_id")
    .eq("id", queueId)
    .single();

  if (fetchError || !queueItem) throw fetchError || new Error("Queue item not found");

  const { error } = await supabase
    .from("content_approval_queue")
    .update({
      status: "rejected",
      rejection_reason: reason,
      reviewed_by: sessionData.session.user.id,
      reviewed_at: new Date().toISOString(),
    })
    .eq("id", queueId);

  if (error) throw error;

  // Deactivate the content and remove from external platforms
  if (queueItem.content_type === "show") {
    await supabase
      .from("shows")
      .update({ status: "paused" })
      .eq("id", queueItem.content_id);

    // Trigger platform removal (fire-and-forget)
    try {
      await supabase.functions.invoke(
        `podgo-automation/podcasts/${queueItem.content_id}`,
        { method: "DELETE" }
      );
    } catch (err) {
      console.error("Failed to remove rejected show from platforms:", err);
    }
  } else if (queueItem.content_type === "episode") {
    await supabase
      .from("episodes")
      .update({ status: "draft" })
      .eq("id", queueItem.content_id);

    // Trigger platform removal (fire-and-forget)
    try {
      await supabase.functions.invoke(
        `podgo-automation/episodes/${queueItem.content_id}`,
        { method: "DELETE" }
      );
    } catch (err) {
      console.error("Failed to remove rejected episode from platforms:", err);
    }
  }

  // Notify the podcaster via email
  try {
    const contentTitle = queueItem.content_type === "show"
      ? (await supabase.from("shows").select("title").eq("id", queueItem.content_id).single()).data?.title
      : (await supabase.from("episodes").select("title").eq("id", queueItem.content_id).single()).data?.title;

    await supabase.functions.invoke("notify-pending-approval", {
      body: {
        type: "content_rejected",
        contentType: queueItem.content_type,
        contentTitle: contentTitle || "Untitled",
        podcasterUserId: queueItem.user_id,
        rejectionReason: reason,
      },
    });
  } catch (err) {
    console.error("Failed to send rejection notification:", err);
  }
}

/**
/**
 * Get approval status for a specific content item (used by podcasters)
 */
export async function getApprovalStatus(
  contentType: "show" | "episode",
  contentId: string
): Promise<ApprovalQueueItem | null> {
  const { data, error } = await supabase
    .from("content_approval_queue")
    .select("*")
    .eq("content_type", contentType)
    .eq("content_id", contentId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return null;
  return mapRow(data);
}

/**
 * Fetch real per-stage timestamps (latest queue row) for a content item.
 * Used by DeliveryTimelineCard to show truthful Pending/Approved times.
 */
export async function getApprovalTimestampsForContent(
  contentType: "show" | "episode",
  contentId: string,
): Promise<{ pendingAt: string | null; approvedAt: string | null }> {
  const { data, error } = await supabase
    .from("content_approval_queue")
    .select("created_at, reviewed_at")
    .eq("content_type", contentType)
    .eq("content_id", contentId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error || !data) return { pendingAt: null, approvedAt: null };
  return {
    pendingAt: data.created_at ?? null,
    approvedAt: data.reviewed_at ?? null,
  };
}

/**
 * Get a single approval queue item with full content details (admin review page)
 */
export async function getApprovalQueueItem(
  queueId: string
): Promise<{ queueItem: ApprovalQueueItem; content: any } | null> {
  const { data, error } = await supabase
    .from("content_approval_queue")
    .select("*")
    .eq("id", queueId)
    .single();

  if (error || !data) return null;

  const queueItem = mapRow(data);

  // Fetch the linked content
  let content: any = null;
  if (data.content_type === "show") {
    const { data: show } = await supabase
      .from("shows")
      .select("*")
      .eq("id", data.content_id)
      .single();
    content = show;
  } else {
    const { data: episode } = await supabase
      .from("episodes")
      .select("*, shows(id, title, cover_url, hosting_platform, contract_signed_at, contract_status, contract_required, docusign_envelope_id)")
      .eq("id", data.content_id)
      .single();
    content = episode;
  }

  // Fetch podcaster profile and reviewer profile in parallel
  const [profileResult, reviewerResult] = await Promise.all([
    supabase.from("profiles").select("display_name, avatar_url").eq("id", data.user_id).single(),
    data.reviewed_by
      ? supabase.from("profiles").select("display_name").eq("id", data.reviewed_by).single()
      : Promise.resolve({ data: null }),
  ]);

  queueItem.podcasterName = profileResult.data?.display_name || "Unknown User";
  queueItem.contentTitle = content?.title || "Unknown";
  if (data.reviewed_by) {
    queueItem.reviewerName = reviewerResult.data?.display_name || "Unknown Admin";
  }

  return { queueItem, content };
}

/**
 * Update ad markers on an episode (admin sets during review)
 */
export async function updateAdMarkers(
  episodeId: string,
  markers: any[]
): Promise<void> {
  const { error } = await supabase
    .from("episodes")
    .update({ ad_markers: markers as any })
    .eq("id", episodeId);

  if (error) throw error;
}

/**
 * Reset an approved/rejected item back to pending (admin only)
 */
export async function resetToPending(queueId: string): Promise<void> {
  const { error } = await supabase
    .from("content_approval_queue")
    .update({
      status: "pending",
      reviewed_by: null,
      reviewed_at: null,
      rejection_reason: null,
      assigned_platforms: [],
    })
    .eq("id", queueId);

  if (error) throw error;
}

/**
 * Delete a queue item (admin only)
 */
export async function deleteQueueItem(queueId: string): Promise<void> {
  const { error } = await supabase
    .from("content_approval_queue")
    .delete()
    .eq("id", queueId);

  if (error) throw error;
}

/**
 * Notify admins about new content pending approval.
 */
export async function notifyAdmins(
  contentType: "show" | "episode",
  contentTitle: string
): Promise<void> {
  try {
    // Resolve podcaster display name from the current user's profile so the
    // admin email shows a real name instead of "Unknown". Mirrors the
    // resolution order used by the edge function's resolveUserName helper.
    let podcasterName: string | undefined;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name, first_name, last_name, podcast_show_name")
          .eq("id", user.id)
          .maybeSingle();
        const fullName = [profile?.first_name, profile?.last_name]
          .filter(Boolean)
          .join(" ")
          .trim();
        podcasterName =
          profile?.display_name?.trim() ||
          fullName ||
          profile?.podcast_show_name?.trim() ||
          user.email?.split("@")[0] ||
          undefined;
      }
    } catch (lookupErr) {
      console.warn("notifyAdmins: failed to resolve podcaster name", lookupErr);
    }

    await supabase.functions.invoke("notify-pending-approval", {
      body: {
        type: "content_pending",
        contentType,
        contentTitle,
        podcasterName,
        submittedAt: new Date().toISOString(),
      },
    });
  } catch (err) {
    console.error("Failed to notify admins:", err);
  }
}
